import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  Utensils, Plus, Trash2, Edit2, Play, CheckCircle2,
  Clock, DollarSign, Layers, ChevronRight, AlertCircle, X
} from 'lucide-react';
import {
  useSubRecipes, useCreateSubRecipe, useUpdateSubRecipe,
  useDeleteSubRecipe, useProduceSubRecipeBatch, useItems
} from '../hooks/useInventory';
import type { SubRecipe, SubRecipeIngredient } from '../api/inventory.client';

export const SubRecipesTab: React.FC = () => {
  const { data: subRecipes = [], isLoading } = useSubRecipes();
  const { data: itemsPage } = useItems({ limit: 1000 });
  const deleteSubRecipeMut = useDeleteSubRecipe();
  const produceBatchMut = useProduceSubRecipeBatch();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSubRecipe, setEditingSubRecipe] = useState<SubRecipe | null>(null);

  const [produceModalOpen, setProduceModalOpen] = useState(false);
  const [producingSubRecipe, setProducingSubRecipe] = useState<SubRecipe | null>(null);
  const [batchQty, setBatchQty] = useState<string>('');
  const [batchNotes, setBatchNotes] = useState<string>('');

  const items = itemsPage?.data || [];

  const handleOpenCreate = () => {
    setEditingSubRecipe(null);
    setEditModalOpen(true);
  };

  const handleOpenEdit = (sr: SubRecipe) => {
    setEditingSubRecipe(sr);
    setEditModalOpen(true);
  };

  const handleDelete = (sr: SubRecipe) => {
    if (window.confirm(`¿Seguro que deseas eliminar la sub-receta "${sr.name}"?`)) {
      deleteSubRecipeMut.mutate(sr.id, {
        onSuccess: () => toast.success('Sub-receta eliminada.'),
        onError: () => toast.error('Error al eliminar sub-receta.'),
      });
    }
  };

  const handleOpenProduce = (sr: SubRecipe) => {
    setProducingSubRecipe(sr);
    setBatchQty(String(sr.yield_qty || 1));
    setBatchNotes('');
    setProduceModalOpen(true);
  };

  const handleConfirmProduce = () => {
    if (!producingSubRecipe) return;
    const numQty = parseFloat(batchQty);
    if (!numQty || numQty <= 0) {
      toast.warning('Ingresa una cantidad válida a producir.');
      return;
    }

    produceBatchMut.mutate(
      {
        id: producingSubRecipe.id,
        data: {
          batch_qty: numQty,
          notes: batchNotes.trim() || undefined,
        },
      },
      {
        onSuccess: (res) => {
          toast.success(res.message || 'Lote producido con éxito.');
          setProduceModalOpen(false);
          setProducingSubRecipe(null);
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message || 'Error al registrar la producción.');
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      {/* Header acciones */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-3xl bg-surface border border-divider shadow-xs">
        <div>
          <h2 className="text-xl font-extrabold text-[color:var(--color-text)] flex items-center gap-2">
            <Utensils className="text-primary" size={24} /> Sub-recetas & Pre-elaboraciones (Mise en Place)
          </h2>
          <p className="text-xs text-muted mt-1">
            Bases, salsas, masas y fondos que se preparan en cocina antes del servicio. Controla su costo y stock real al producir cada lote.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="px-5 py-2.5 rounded-xl bg-primary text-white text-xs font-bold shadow-md hover:opacity-90 transition flex items-center gap-2 shrink-0 self-start sm:self-auto"
        >
          <Plus size={16} /> Nueva Sub-receta
        </button>
      </div>

      {/* Grid de Sub-recetas */}
      {isLoading ? (
        <div className="py-20 text-center text-muted text-xs">Cargando sub-recetas...</div>
      ) : subRecipes.length === 0 ? (
        <div className="py-16 text-center text-muted border border-dashed border-divider rounded-3xl p-8 space-y-3">
          <Utensils size={40} className="mx-auto text-muted/30" />
          <h4 className="text-sm font-bold text-[color:var(--color-text)]">No tienes sub-recetas configuradas</h4>
          <p className="text-xs max-w-md mx-auto">
            Crea tu primera sub-receta (ej: Salsa Pomodoro, Masa de Pizza, Almíbar) para costearla y producir lotes que descuenten insumos crudos automáticamente.
          </p>
          <button
            onClick={handleOpenCreate}
            className="px-4 py-2 rounded-xl bg-primary text-white text-xs font-bold shadow-sm inline-flex items-center gap-2"
          >
            <Plus size={14} /> Crear Primera Sub-receta
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {subRecipes.map((sr) => (
            <div
              key={sr.id}
              className="rounded-3xl border border-divider bg-surface p-5 shadow-xs hover:border-primary/40 transition flex flex-col justify-between space-y-4"
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-base font-extrabold text-[color:var(--color-text)]">
                      {sr.name}
                    </h3>
                    <span className="text-[11px] text-muted font-medium block">
                      Produce: <strong className="text-[color:var(--color-text)]">{sr.produced_item?.name || 'Insumo'}</strong>
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenEdit(sr)}
                      className="p-1.5 rounded-lg text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)] transition"
                      title="Editar"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(sr)}
                      className="p-1.5 rounded-lg text-muted hover:bg-red-500/10 hover:text-red-500 transition"
                      title="Eliminar"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Métricas clave */}
                <div className="grid grid-cols-2 gap-2 p-3 rounded-2xl bg-surface-muted/40 border border-divider text-xs">
                  <div>
                    <span className="text-muted text-[10px] uppercase font-bold block">Rendimiento Base</span>
                    <strong className="text-[color:var(--color-text)] font-mono font-bold">
                      {sr.yield_qty} {sr.yield_unit}
                    </strong>
                  </div>
                  <div>
                    <span className="text-muted text-[10px] uppercase font-bold block">Costo Teórico / {sr.yield_unit}</span>
                    <strong className="text-emerald-600 font-mono font-bold">
                      ${(sr.theoretical_cost_per_unit || 0).toLocaleString()}
                    </strong>
                  </div>
                </div>

                {/* Ingredientes lista resumida */}
                <div className="text-xs space-y-1">
                  <span className="text-[11px] font-bold text-muted uppercase tracking-wider block">
                    Ingredientes ({sr.ingredients?.length || 0}):
                  </span>
                  <div className="space-y-1 max-h-24 overflow-y-auto pr-1">
                    {sr.ingredients?.map((ing, i) => (
                      <div key={i} className="flex justify-between text-[11px] text-muted">
                        <span>• {ing.inventory_item?.name || 'Ingrediente'}</span>
                        <span className="font-mono">{ing.qty} {ing.unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Botón Producir Lote */}
              <div className="pt-2 border-t border-divider">
                <button
                  onClick={() => handleOpenProduce(sr)}
                  className="w-full py-2.5 rounded-xl bg-primary/10 hover:bg-primary hover:text-white text-primary text-xs font-bold transition flex items-center justify-center gap-2 shadow-2xs"
                >
                  <Play size={14} /> Producir Lote (Mise en Place)
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Producir Lote */}
      {produceModalOpen && producingSubRecipe && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-md rounded-3xl bg-surface border border-divider p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-divider pb-3">
              <div className="flex items-center gap-2 text-primary font-bold">
                <Play size={20} />
                <h3 className="text-base text-[color:var(--color-text)]">
                  Producir Lote: {producingSubRecipe.name}
                </h3>
              </div>
              <button
                onClick={() => setProduceModalOpen(false)}
                className="p-1 rounded-lg text-muted hover:bg-surface-muted"
              >
                <X size={18} />
              </button>
            </div>

            <p className="text-xs text-muted">
              Al confirmar, el sistema descontará automáticamente los ingredientes crudos del inventario y sumará la cantidad producida al stock de <strong>{producingSubRecipe.produced_item?.name}</strong> actualizando su costo unitario exacto.
            </p>

            <div>
              <label className="block text-xs font-semibold text-muted mb-1">
                Cantidad a Producir ({producingSubRecipe.yield_unit}) *
              </label>
              <input
                type="number"
                step="any"
                min="0.001"
                value={batchQty}
                onChange={(e) => setBatchQty(e.target.value)}
                className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm font-bold font-mono focus:ring-2 focus:ring-primary"
                placeholder="Ej: 5.0"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted mb-1">
                Notas del lote (opcional)
              </label>
              <input
                type="text"
                value={batchNotes}
                onChange={(e) => setBatchNotes(e.target.value)}
                placeholder="Ej: Preparado para turno noche."
                className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-xs focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setProduceModalOpen(false)}
                className="px-4 py-2 rounded-xl border border-divider text-xs font-bold hover:bg-surface-muted"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmProduce}
                disabled={produceBatchMut.isPending}
                className="px-5 py-2 rounded-xl bg-primary text-white text-xs font-bold shadow-md hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
              >
                {produceBatchMut.isPending ? 'Procesando...' : 'Confirmar Producción'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear / Editar Sub-receta */}
      {editModalOpen && (
        <SubRecipeEditorModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          subRecipe={editingSubRecipe}
          items={items}
        />
      )}
    </div>
  );
};

// Modal de Creación / Edición de Sub-receta
interface SubRecipeEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  subRecipe: SubRecipe | null;
  items: any[];
}

const SubRecipeEditorModal: React.FC<SubRecipeEditorModalProps> = ({
  isOpen, onClose, subRecipe, items
}) => {
  const [name, setName] = useState(subRecipe?.name || '');
  const [inventoryItemId, setInventoryItemId] = useState(String(subRecipe?.inventory_item_id || ''));
  const [yieldQty, setYieldQty] = useState(String(subRecipe?.yield_qty || '1'));
  const [yieldUnit, setYieldUnit] = useState(subRecipe?.yield_unit || 'litros');
  const [instructions, setInstructions] = useState(subRecipe?.instructions || '');
  const [ingredients, setIngredients] = useState<Array<{
    inventory_item_id: number;
    qty: number;
    unit: string;
    waste_pct: number;
    notes?: string;
  }>>(
    subRecipe?.ingredients?.map((ing) => ({
      inventory_item_id: ing.inventory_item_id,
      qty: ing.qty,
      unit: ing.unit,
      waste_pct: ing.waste_pct || 0,
      notes: ing.notes || '',
    })) || []
  );

  const createMut = useCreateSubRecipe();
  const updateMut = useUpdateSubRecipe();

  const handleAddIngredient = () => {
    if (items.length === 0) return;
    setIngredients((prev) => [
      ...prev,
      {
        inventory_item_id: Number(items[0].id),
        qty: 1,
        unit: items[0].unit || 'und',
        waste_pct: 0,
        notes: '',
      },
    ]);
  };

  const handleRemoveIngredient = (idx: number) => {
    setIngredients((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpdateIngredient = (idx: number, field: string, val: any) => {
    setIngredients((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: val };
      return copy;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.warning('Ingresa el nombre de la sub-receta.');
      return;
    }
    if (!inventoryItemId) {
      toast.warning('Selecciona el insumo de inventario que representa esta sub-receta.');
      return;
    }
    if (ingredients.length === 0) {
      toast.warning('Agrega al menos un ingrediente crudo a la sub-receta.');
      return;
    }

    const payload = {
      name: name.trim(),
      inventory_item_id: Number(inventoryItemId),
      yield_qty: parseFloat(yieldQty) || 1.0,
      yield_unit: yieldUnit.trim() || 'und',
      instructions: instructions.trim() || undefined,
      ingredients,
    };

    if (subRecipe) {
      updateMut.mutate(
        { id: subRecipe.id, data: payload },
        {
          onSuccess: () => {
            toast.success('Sub-receta actualizada.');
            onClose();
          },
          onError: () => toast.error('Error al actualizar sub-receta.'),
        }
      );
    } else {
      createMut.mutate(payload, {
        onSuccess: () => {
          toast.success('Sub-receta creada exitosamente.');
          onClose();
        },
        onError: () => toast.error('Error al crear sub-receta.'),
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-3xl bg-surface border border-divider shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-divider bg-surface-muted/30">
          <h3 className="text-base font-extrabold text-[color:var(--color-text)] flex items-center gap-2">
            <Utensils className="text-primary" size={18} />
            {subRecipe ? 'Editar Sub-receta' : 'Nueva Sub-receta / Mise en Place'}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg text-muted hover:bg-surface-muted">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-muted mb-1">
                Nombre de la preparación *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Salsa Pomodoro Artesanal"
                className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-xs focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted mb-1">
                Insumo Producido en Inventario *
              </label>
              <select
                value={inventoryItemId}
                onChange={(e) => {
                  setInventoryItemId(e.target.value);
                  const sel = items.find((i) => String(i.id) === e.target.value);
                  if (sel?.unit) setYieldUnit(sel.unit);
                }}
                className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-xs focus:ring-2 focus:ring-primary"
                required
              >
                <option value="">-- Selecciona el insumo producido --</option>
                {items.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.name} ({it.unit})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-muted mb-1">
                Rendimiento del lote *
              </label>
              <input
                type="number"
                step="any"
                min="0.001"
                value={yieldQty}
                onChange={(e) => setYieldQty(e.target.value)}
                placeholder="Ej: 5.0"
                className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-xs font-mono font-bold focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted mb-1">
                Unidad de rendimiento *
              </label>
              <input
                type="text"
                value={yieldUnit}
                onChange={(e) => setYieldUnit(e.target.value)}
                placeholder="litros, kg, porciones..."
                className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-xs focus:ring-2 focus:ring-primary"
                required
              />
            </div>
          </div>

          {/* Ingredientes crudos */}
          <div className="space-y-2 pt-2 border-t border-divider">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-[color:var(--color-text)] uppercase tracking-wider">
                Ingredientes Crudos Requeridos
              </span>
              <button
                type="button"
                onClick={handleAddIngredient}
                className="px-3 py-1 rounded-lg bg-surface-muted border border-divider text-xs font-semibold hover:border-primary transition flex items-center gap-1"
              >
                <Plus size={13} /> Añadir Ingrediente
              </button>
            </div>

            {ingredients.length === 0 ? (
              <p className="text-xs text-muted text-center py-4 border border-dashed border-divider rounded-xl">
                Añade los ingredientes que componen esta sub-receta.
              </p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {ingredients.map((ing, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 rounded-xl bg-surface-muted/30 border border-divider text-xs">
                    <select
                      value={ing.inventory_item_id}
                      onChange={(e) => {
                        const id = Number(e.target.value);
                        const sel = items.find((i) => i.id === id);
                        handleUpdateIngredient(idx, 'inventory_item_id', id);
                        if (sel?.unit) handleUpdateIngredient(idx, 'unit', sel.unit);
                      }}
                      className="flex-1 rounded-lg border border-divider bg-surface px-2 py-1.5 text-xs focus:ring-2 focus:ring-primary"
                    >
                      {items.map((it) => (
                        <option key={it.id} value={it.id}>
                          {it.name}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      step="any"
                      min="0.0001"
                      value={ing.qty}
                      onChange={(e) => handleUpdateIngredient(idx, 'qty', parseFloat(e.target.value) || 0)}
                      placeholder="Cant."
                      className="w-20 rounded-lg border border-divider bg-surface px-2 py-1.5 text-xs text-center font-mono font-bold"
                    />

                    <input
                      type="text"
                      value={ing.unit}
                      onChange={(e) => handleUpdateIngredient(idx, 'unit', e.target.value)}
                      placeholder="g, ml, und..."
                      className="w-16 rounded-lg border border-divider bg-surface px-2 py-1.5 text-xs text-center"
                    />

                    <div className="flex items-center gap-1" title="Merma %">
                      <span className="text-[10px] text-muted">%M:</span>
                      <input
                        type="number"
                        min="0"
                        max="99"
                        value={ing.waste_pct}
                        onChange={(e) => handleUpdateIngredient(idx, 'waste_pct', parseFloat(e.target.value) || 0)}
                        className="w-12 rounded-lg border border-divider bg-surface px-1 py-1.5 text-xs text-center font-mono"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveIngredient(idx)}
                      className="p-1 text-muted hover:text-red-500 rounded"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-divider flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-divider text-xs font-bold hover:bg-surface-muted"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMut.isPending || updateMut.isPending}
              className="px-5 py-2 rounded-xl bg-primary text-white text-xs font-bold shadow-md hover:opacity-90 disabled:opacity-50"
            >
              {subRecipe ? 'Guardar Cambios' : 'Crear Sub-receta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
